import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { DemoGallery } from "./demo/Gallery";
import { isDemoPath } from "./demo/route";
import { SessionProvider } from "./session/SessionProvider";
import { ControlsProvider } from "./shell/Controls";

// The one stylesheet. Imported here rather than linked from index.html so that
// the design system is part of the module graph — vite then fails the build on
// a missing token file instead of serving a page with no styles at all, which
// is a failure that presents as a rendering bug three people away from its
// cause.
import "./design/index.css";

const host = document.getElementById("root");
if (!host) throw new Error("#root is missing from index.html");

// ---------------------------------------------------------------------------
// THE DEMO GALLERY IS A SEPARATE ROOT, AND THIS IS GUARD 1 OF 3.
//
// `import.meta.env.VITE_DEMO` is substituted at build time, so in a normal
// build this is `false`, the branch is dead code, `DemoGallery` is unreferenced
// and rollup drops every module under web/src/demo. vite.config.ts declares
// that tree side-effect-free so the drop is complete rather than partial — it
// was partial once, and a production bundle shipped a fabricated organisation
// while the grep for the gallery's own marker came back clean.
// scripts/verify-demo-absent.sh builds BOTH ways and proves it.
//
// WHY IT IS NOT A ROUTE INSIDE App. A gallery cell renders a screen at a URL
// that has nothing to do with the browser's — /console/clients/{id} while the
// address bar says /demo/... — and the only way React Router permits that is
// for the cell's MemoryRouter to be the one and only Router in its tree.
// Inside App's BrowserRouter it cannot be. demo/route.ts records the two
// arrangements that were tried and refused.
//
// It is also the stronger guard: the product's router now has no /demo route at
// all, so a leaked module would still not be reachable through App.
// ---------------------------------------------------------------------------
if (import.meta.env.VITE_DEMO === "1" && isDemoPath(window.location.pathname)) {
  createRoot(host).render(
    <StrictMode>
      <DemoGallery />
    </StrictMode>,
  );
} else {
  createRoot(host).render(
    <StrictMode>
      <BrowserRouter>
        <SessionProvider>
          <ControlsProvider>
            <App />
          </ControlsProvider>
        </SessionProvider>
      </BrowserRouter>
    </StrictMode>,
  );
}

// ---------------------------------------------------------------------------
// V34 — THE BROWSER CHROME COLOUR, FROM THE TOKEN AND NOT FROM A COPY.
//
// An installed app paints its status bar and its task-switcher card with
// `<meta name="theme-color">`. Writing a hex into index.html is a second copy of
// a token, and `verify-design-parity.sh` refuses a colour literal outside the
// token files — it refused this one on the first build. Reading the computed
// value means the chrome and the stylesheet cannot disagree.
//
// THE SEMANTIC TOKEN, NOT THE PRIMITIVE. The same guard refused the primitive on
// the second build, also correctly: a frame is an ACTION surface, and naming the
// clay directly is how a status colour eventually ends up somewhere it decorates
// rather than states something.
// ---------------------------------------------------------------------------
{
  const brand = getComputedStyle(document.documentElement).getPropertyValue("--action").trim();
  if (brand.length > 0) {
    const meta = document.createElement("meta");
    meta.name = "theme-color";
    meta.content = brand;
    document.head.appendChild(meta);
  }
}

// ---------------------------------------------------------------------------
// V34 — THE SERVICE WORKER.
//
// REGISTERED LAST, AFTER THE APP HAS MOUNTED, and never in the gallery. The
// gallery is a no-login surface that must be provably absent from production
// and must run from a static build with the network disabled; giving it a
// service worker would put a second cache in front of the one thing whose
// contents `verify-demo-absent.sh` inspects.
//
// IT IS ALSO NOT REGISTERED IN DEVELOPMENT. A cached shell in front of vite's
// dev server is how somebody spends an afternoon editing a file that is not
// being served, and this repository has lost an afternoon to a stale bundle
// before.
// ---------------------------------------------------------------------------
if (!import.meta.env.DEV && !isDemoPath(window.location.pathname) && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // A HANDSET THAT REFUSES ONE STILL WORKS, with a network. There is nothing
      // to tell the user here and nothing to retry: the offline queue reports
      // its own availability on the screen that uses it.
    });
  });
}
